import type { ReactNode } from 'react'
import { KaproLogo } from '@/components/Logo'
import { LoginIllustration } from '@/components/LoginIllustration'
import { ThemeToggle } from '@/components/ThemeProvider'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'

/**
 * The split-panel frame around every signed-out screen.
 *
 * Extracted when password recovery arrived and there were suddenly three of
 * these — login, forgot, reset — rather than one. Sharing the frame is not just
 * less code: somebody who clicks "forgot password" has to stay obviously inside
 * the same application, and three hand-built variants of a brand panel drift
 * apart within a month.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex min-h-dvh flex-col">
      {/*
        Theme switch, before anyone has an account to switch it on.

        The header's copy is inside the signed-in shell, so until now the only
        way to reach a theme was to sign in first — which is backwards for the
        night shift, who meet a full-brightness white page in a dim workshop and
        have to get through it before they can turn the lights down.

        Absolutely positioned, so it costs the layout no height. These screens
        were measured to the pixel to stop them scrolling and a toggle in the
        flow would have put them straight back over the fold.
      */}
      {/*
        The language switch sits beside the theme switch, and for the same
        reason it was put here: until now the only way to reach either was to
        sign in first, which is backwards for the one person who needs the
        language control most — somebody looking at a sign-in screen in a
        language they do not read, with no way past it.
      */}
      <div className="absolute top-3 right-3 z-10 flex items-center gap-1">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>

      <div className="grid flex-1 lg:grid-cols-2">
        {/*
          Brand panel — desktop only.

          It spent a while collapsing to a band on phones instead of
          disappearing. That is off again: on a phone the drum is decoration
          sitting directly above the only thing anyone came here for, and the
          band was pushing the password field down for no return. A worker
          signing in beside a machine wants two boxes and a button.

          The panel is plum, not the brand blue, because the drum is warm orange
          wood — against blue it would sit in the same cool register and go flat.
        */}
        <aside className="relative hidden items-center justify-center overflow-hidden bg-gradient-to-br from-[#342b52] to-[#1b1930] px-6 py-8 lg:flex lg:py-16">
          {/* Depth without an image to download. */}
          <div
            className="pointer-events-none absolute -top-32 -right-24 size-96 rounded-full bg-white/[0.07] blur-3xl"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute -bottom-40 -left-24 size-[28rem] rounded-full bg-black/20 blur-3xl"
            aria-hidden
          />

          <LoginIllustration className="relative max-h-40 w-auto max-w-md lg:max-h-[28rem] lg:w-full" />
        </aside>

        {/* Form panel */}
        {/*
          Tighter vertical padding than the usual py-16: with the footer below
          it, the extra space was the difference between the sign-in button
          being on screen and needing a scroll.

          The height query is doing the last few pixels of that. A 1280×720
          laptop is the tightest real screen this runs on and it was overflowing
          by single digits; rather than shrink the padding for everyone, short
          viewports give some back. Keyed on height, not width, because height
          is the thing that ran out.
        */}
        <main className="flex items-center justify-center px-6 py-6 sm:py-8 lg:py-10 [@media(max-height:820px)]:lg:py-5">
          <div className="w-full max-w-sm">{children}</div>
        </main>
      </div>

      {/*
        Footer bar, full width under both panels.

        One row, not two. The credit started life as a second row under a rule
        and that pushed the whole page past the fold — on a login screen, where
        the only thing anyone needs is above it, making the page scroll to reach
        a byline is the wrong trade. Everything now sits on one line and wraps
        to a centred stack only on a narrow screen.
      */}
      <footer className="bg-[#12101d] px-6 py-3 text-xs text-slate-400 sm:py-4">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-x-6 gap-y-1.5 text-center sm:flex-row sm:justify-between sm:text-left">
          {/*
            The product, not the customer.

            Livprom's mark used to sit here, which was right when this was one
            company's internal application and is wrong now that it is a product
            several companies sign in to. Nobody has identified themselves at
            this point — the sign-in screen is reached before anyone knows which
            customer is looking at it — so a customer's logo here would be a
            guess, and on a shared host it would be the wrong guess most of the
            time.

            Their branding moves to the dashboard, where the account is known.
          */}
          <KaproLogo size="sm" className="shrink-0 text-slate-200" />

          <p className="leading-relaxed">
            © {new Date().getFullYear()} KaProLean
            {/* The access notice wraps to two more lines at 375px, and on a
                phone those two lines are the difference between the page
                sitting still and scrolling. It is a notice, not a control. */}
            <span className="hidden sm:inline">
              {' '}
              — interna aplikacija, pristup samo za zaposlene.
            </span>
          </p>

          {/* External links, so noopener/noreferrer — without them the opened
              tab gets a handle on this one through window.opener. */}
          <p className="shrink-0">
            Softver:{' '}
            <a
              href="https://kala.rs"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-slate-200 underline-offset-4 hover:underline"
            >
              Kala Consulting
            </a>{' '}
            &{' '}
            <a
              href="https://yanoka.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-slate-200 underline-offset-4 hover:underline"
            >
              Yanoka
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}
